import React, { Fragment, useCallback, useState, useMemo, useEffect } from "react";
import styles from "./Landing.module.css";
import { Chip, ListItem } from '@mui/material';
import resetBtn from "../../../../assets/images/resetBtn.png";
import Filters from "../../../../assets/images/filters.svg";
import Slider from "../../../../assets/images/sliders 1.svg";
import Excel from "../../../../assets/images/Excel.svg";
import { useDailyPhasingContext } from "../../../../utils/dailyphaisngContext/DailyphasingContext";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu";
import MMScreenView from "./MMScreenInputViews/MMScreenView";
import MMInputView from "./MMScreenInputViews/MMInputView";
import MoreFilters from "Samarth Module/components/drawers/moreFilters";
import CustomiseColumns from 'Samarth Module/components/drawers/CustomiseColumns';
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import { getWeekFilterOptions } from "utils/mmScreenInput";

const MMScreenInputLanding = ({ exportToExcel, activeAccount }, ref) => {
    const { globalFilters, tableDataColumns, editableTableDataColumns, setGlobalFilters, setEditableTableFilters, filteredTableData, tableData, editableTableData, addedRows, setTableDataColumns, setEditableTableDataColumns, selectedWeek, weekChangeHandler } = useDailyPhasingContext();
    const [isCustomizeDrawerOpen, setIsCustomizeDrawerOpen] = useState(false);
    const [isMoreFilterDrawerOpen, setIsMoreFilterDrawerOpen] = useState(false);
    const [isOpen, setIsOpen] = useState({"tbl1": true, "tbl2": true});
    
    // Get week filter options dynamically
    const weekOptions = useMemo(() => getWeekFilterOptions(), []);
    
    // Initialize selectedWeek from context or default to first week
    useEffect(() => {
        if (!selectedWeek && weekOptions.length > 0) {
            weekChangeHandler(weekOptions[0]);
        }
    }, [weekOptions, selectedWeek, weekChangeHandler]);
    
    // Create Week filter object
    const weekFilter = useMemo(() => ({
        label: "Week",
        data: weekOptions,
        selectedData: selectedWeek || weekOptions[0] || "", // Use context value or default to first week
        isFavourite: true,
        isMultiSelect: false
    }), [weekOptions, selectedWeek]);
    
    // Combine Week filter with global filters, ensuring Week is always first
    const allFilters = useMemo(() => {
        const nonWeekFilters = globalFilters.filter(f => f.label !== "Week");
        return [weekFilter, ...nonWeekFilters];
    }, [globalFilters, weekFilter]);

    // Toggle customize drawer
    const toggleCustomizeDrawer = useCallback(() => {
        setIsCustomizeDrawerOpen(prev => !prev);
    }, []);

    // submitting column customization
    const columnCustomization = useCallback((list) => {
        setTableDataColumns(list["Table 1"]);
        setEditableTableDataColumns(list["Table 2"]);
        setIsCustomizeDrawerOpen(prev => !prev);
    }, []);

    // Toggle filter drawer
    const toggleFilterDrawer = useCallback(() => {
        setIsMoreFilterDrawerOpen(prev => !prev);
    }, []);

    // Reset global filters
    const resetFilters = useCallback(() => {
        setGlobalFilters(prev =>
            prev.map(filter => ({ ...filter, selectedData: [] }))
        );
        setEditableTableFilters(prev =>
            prev.map(filter => ({ ...filter, selectedData: [] }))
        )
    }, [setGlobalFilters, setEditableTableFilters]);

    const handleGlobalFilterChange = useCallback((label, values) => {
        if (label === "Week") {
            // Store Week value in context
            weekChangeHandler(values);
            return;
        }
        setGlobalFilters(prevFilters =>
            prevFilters.map(filter =>
                filter.label === label ? { ...filter, selectedData: values } : filter
            )
        );
    }, [setGlobalFilters, weekChangeHandler]);

    // Calculate dynamically filtered options for each filter based on other filters
    const getFilteredOptionsForFilter = useCallback((currentFilterLabel) => {
        // Week filter uses static options
        if (currentFilterLabel === "Week") {
            return weekOptions;
        }
        
        // Get all data sources
        const allData = [...tableData, ...editableTableData, ...addedRows];
        
        if (!allData.length) {
            return globalFilters.find(f => f.label === currentFilterLabel)?.data || [];
        }

        // Apply all filters except the current one
        const otherFilters = globalFilters.filter(f => f.label !== currentFilterLabel);
        
        let filteredData = allData;
        otherFilters.forEach(filter => {
            if (filter.selectedData && filter.selectedData.length > 0) {
                filteredData = filteredData.filter(item => 
                    filter.selectedData.includes(item[filter.label])
                );
            }
        });

        // Extract unique values for current filter from filtered data
        const availableOptions = Array.from(
            new Set(filteredData.map(item => item[currentFilterLabel]))
        ).filter(val => val !== undefined && val !== null)
        .sort((a, b) => String(a).localeCompare(String(b)));

        return availableOptions;
    }, [globalFilters, tableData, editableTableData, addedRows, weekOptions, weekChangeHandler]);

    // Memoized filtered options for all filters
    const dynamicFilterOptions = useMemo(() => {
        const options = {};
        allFilters.forEach(filter => {
            options[filter.label] = getFilteredOptionsForFilter(filter.label);
        });
        return options;
    }, [allFilters, getFilteredOptionsForFilter]);

    const handleExportToExcel = () => {
        exportToExcel(filteredTableData);
    }

    const handleToggle = (tableNum) => {
        setIsOpen((prev) => ({...prev , [tableNum]: !prev[tableNum] }));
    };

    return (
        <Fragment>
            <div className={styles.filters} style={{ width: 'auto' }}>
                {Array.isArray(allFilters) && allFilters.length > 0 && allFilters.filter((filter) => filter.isFavourite).map((filter) => (
                    <ListItem key={filter.label} className={styles.listCustomClass}>
                        <MultiSelectWithSearchInput
                            key={`${filter.label}`}
                            label={filter.label}
                            options={dynamicFilterOptions[filter.label] || filter.data}
                            filterValues={filter.selectedData}
                            handleFilter={(label, values) => handleGlobalFilterChange(label, values)}
                            disabled={false}
                            type={'MM Screen Input'}
                            isMultiSelect={filter.isMultiSelect !== false} // Default to true unless explicitly false
                            defaultValue={filter.label === 'Week' ? weekOptions[0] : null}
                        />
                    </ListItem>
                ))}
                <div style={{ paddingLeft: '5px', display: 'flex', alignItems: 'center' }}>
                    <img src={resetBtn} onClick={resetFilters} alt="Reset Filters" style={{ cursor: 'pointer' }} />
                    <OverflowMenu
                        menuItems={[
                            {
                                label: 'More Filters',
                                icon: Filters,
                                onClick: toggleFilterDrawer,
                            },
                            {
                                label: 'Customise',
                                icon: Slider,
                                onClick: toggleCustomizeDrawer,
                            },
                            {
                                label: 'Export',
                                icon: Excel,
                                onClick: handleExportToExcel,
                            },
                        ]}
                    />
                </div>
            </div>
            {/* filter chip */}
            {Array.isArray(globalFilters) && globalFilters.length > 0 && globalFilters.filter((filter) => !filter.isFavourite).map((filter, index) => (
                <div className={styles.chipContainer} key={`${index}_${filter.label}`}>
                    {filter.selectedData.map((selectedValue, subIndex) => (
                        <Chip
                            key={`${index}-${subIndex}`}
                            label={selectedValue}
                            onDelete={() => {
                                const updatedFilters = [...globalFilters].map((f) =>
                                    f.label === filter.label
                                        ? {
                                            ...f,
                                            selectedData: f.selectedData.filter((_, i) => i !== subIndex),
                                        }
                                        : f
                                );
                                setGlobalFilters(updatedFilters);
                            }}
                        />
                    ))}
                </div>
            ))}
            <div className={styles.tableWrapper}>
                {/* Table 1: CBU level plan */}
                {isOpen["tbl1"] && (
                    <div className={styles.flexTable}>
                        <div className={styles.collapHead}>
                            <div className={styles.collapHeadIconBox} onClick={() => handleToggle("tbl1")}>
                                <MdKeyboardArrowUp /> CBU level plan
                            </div>
                        </div>
                        <MMScreenView />
                    </div>
                )}
                {!isOpen["tbl1"] && (
                    <div className={styles.collapHead}>
                        <div className={styles.collapHeadIconBox} onClick={() => handleToggle("tbl1")}>
                            <MdKeyboardArrowDown /> CBU level plan
                        </div>
                    </div>
                )}

                {/* Table 2: Line-CBU level plan */}
                {isOpen["tbl2"] && (
                    <div className={styles.flexTable}>
                        <div className={styles.collapHead}>
                            <div className={styles.collapHeadIconBox} onClick={() => handleToggle("tbl2")}>
                                <MdKeyboardArrowUp /> Line-CBU level plan
                            </div>
                        </div>
                        <MMInputView 
                            username={activeAccount.username}
                            exportToExcel={exportToExcel}
                        />
                    </div>
                )}
                {!isOpen["tbl2"] && (
                    <div className={styles.collapHead}>
                        <div className={styles.collapHeadIconBox} onClick={() => handleToggle("tbl2")}>
                            <MdKeyboardArrowDown /> Line-CBU level plan
                        </div>
                    </div>
                )}
            </div>

            {/* more filters for mm input */}
            {isMoreFilterDrawerOpen && (
                <MoreFilters
                    anchor="right"
                    filterDrawer={isMoreFilterDrawerOpen}
                    handleFilterDrawer={toggleFilterDrawer}
                    rmpmFilters={globalFilters}
                    setRmpmFilters={setGlobalFilters}
                    username={activeAccount.username}
                    type="MM Screen Input"
                />
            )}
            {/* customize column for mm input */}
            {isCustomizeDrawerOpen && (
                <CustomiseColumns
                    anchor={'right'}
                    columns={{ "Table 1": tableDataColumns, "Table 2": editableTableDataColumns }}
                    isOpen={isCustomizeDrawerOpen}
                    handleCustomizeDrawer={toggleCustomizeDrawer}
                    defaultColumns={{
                        "Table 1": ["Reason for change", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun", "Total"],
                        "Table 2": ["Line", "Cascade", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun", "Total"]
                    }}
                    columnCustomization={(customizedColumns) => columnCustomization(customizedColumns)}
                    username={activeAccount.username}
                    type="MM Screen Input"
                />
            )}
        </Fragment>
    )
}

export default React.memo(MMScreenInputLanding);


